import { Alert, Button, Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import axios from "../../../config/axios";

// how many upcoming delivery days the customer gets to choose from
const DATES_SHOWN = 8;

// a german postal code, the shape the lookup needs before it is worth asking
const ZIP_PATTERN = /^\d{5}$/;

// below this the date button says how little room is left
const LOW_CAPACITY_HINT = 5;

const formatDate = (isoDate) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString("de-DE", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

export const DeliveryDates = ({ zipCode, value, onChange, onStatusChange }) => {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [dates, setDates] = useState([]);
  const [isCovered, setIsCovered] = useState(true);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const trimmedZip = (zipCode || "").trim();
  const isLookupZip = ZIP_PATTERN.test(trimmedZip);

  // the newest lookup wins: the customer types the postal code digit by digit,
  // so a slower earlier response must not overwrite a later one
  const lookupRef = useRef(0);

  useEffect(() => {
    if (!isLookupZip) {
      // the postal code field starts out empty on every mount, so a date
      // restored from an earlier visit is left alone here - it is only dropped
      // once a lookup comes back without it
      setDates([]);
      setIsCovered(true);
      setFailed(false);
      return;
    }

    const lookup = ++lookupRef.current;
    setLoading(true);

    // waits out the rest of the postal code before asking the server
    const timer = setTimeout(() => {
      axios
        .get("/public/delivery/available-dates", {
          params: { zip_code: trimmedZip, limit: DATES_SHOWN },
        })
        .then((response) => {
          if (lookup !== lookupRef.current) return;

          const available = response.data.dates || [];
          setDates(available);
          setIsCovered(response.data.is_covered);
          setFailed(false);

          // the date held from before is only kept while it is still offered
          const stillOffered = available.some((entry) => entry.date === value);
          if (!stillOffered) {
            onChange?.(available.length > 0 ? available[0].date : null);
          }
        })
        .catch(() => {
          if (lookup !== lookupRef.current) return;
          setDates([]);
          setFailed(true);
          onChange?.(null);
        })
        .finally(() => {
          if (lookup === lookupRef.current) setLoading(false);
        });
    }, 400);

    return () => clearTimeout(timer);
    // value is left out on purpose: picking a date must not refetch the list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedZip, isLookupZip]);

  // what the checkout needs to know to decide whether it can move on:
  //   idle      - no usable postal code typed yet
  //   uncovered - no delivery plan serves this postal code
  //   full      - the area is served, but every upcoming day is booked out
  //   failed    - the lookup did not come back
  //   available - there are dates to pick from
  const status = !isLookupZip
    ? "idle"
    : failed
      ? "failed"
      : !isCovered
        ? "uncovered"
        : dates.length === 0
          ? "full"
          : "available";

  useEffect(() => {
    onStatusChange?.(loading ? "loading" : status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, loading]);

  const renderDates = () => {
    if (!isLookupZip) {
      return (
        <span style={{ color: "#8c8c8c" }}>
          Add your shipping postal code above to see the delivery dates.
        </span>
      );
    }

    if (failed) {
      return (
        <Alert
          type="warning"
          showIcon
          message="We could not load the delivery dates. Please try again in a moment."
        />
      );
    }

    if (!isCovered) {
      return (
        <Alert
          type="info"
          showIcon
          message={`We do not deliver to ${trimmedZip} yet.`}
          description="Please check the postal code, or get in touch with us about your area."
        />
      );
    }

    if (dates.length === 0) {
      return (
        <Alert
          type="info"
          showIcon
          message="All delivery days for your area are fully booked at the moment."
          description="Please try again later, we open up new days as they free up."
        />
      );
    }

    return (
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
        {dates.map((entry) => (
          <Button
            key={entry.date}
            type={value === entry.date ? "primary" : "default"}
            onClick={() => onChange?.(entry.date)}
            style={{ height: "auto", padding: "0.5rem 1rem" }}
          >
            <div style={{ display: "block" }}>
              <div>{formatDate(entry.date)}</div>
              {entry.remaining_capacity !== null &&
                entry.remaining_capacity <= LOW_CAPACITY_HINT && (
                  <div style={{ fontSize: "1.1rem", opacity: 0.75 }}>
                    {entry.remaining_capacity} left
                  </div>
                )}
            </div>
          </Button>
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding: "1rem 0" }}>
      <div style={{ marginBottom: "1.5rem" }}>
        <span
          style={{ fontWeight: 600, marginBottom: "0.5rem", display: "block" }}
        >
          Select Delivery Date
        </span>
        <Spin spinning={loading}>{renderDates()}</Spin>
      </div>

      <div>
        <span
          style={{ fontWeight: 600, marginBottom: "0.5rem", display: "block" }}
        >
          Select Time Slot
        </span>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {["12:00 - 20:00"].map((slot) => (
            <Button
              disabled
              key={slot}
              type={selectedSlot === slot ? "primary" : "default"}
              onClick={() => setSelectedSlot(slot)}
            >
              {slot}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};
